import React from 'react';
import Icon from 'components/AppIcon';

const DemographicsSection = ({ data = {}, onChange, errors = [] }) => {
  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleMultiSelectChange = (field, value, checked) => {
    const currentValues = data[field] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);
    onChange({ [field]: newValues });
  };

  const genderOptions = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
    { value: 'other', label: 'Other' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  const livingSituationOptions = [
    { value: 'alone', label: 'Living alone' },
    { value: 'family', label: 'Living with family' },
    { value: 'roommates', label: 'Living with roommates' },
    { value: 'partner', label: 'Living with partner/spouse' },
    { value: 'care-facility', label: 'In a care facility' }
  ];

  const employmentOptions = [
    { value: 'employed-office', label: 'Employed (working from office)' },
    { value: 'employed-home', label: 'Employed (working from home)' },
    { value: 'unemployed', label: 'Unemployed' },
    { value: 'student', label: 'Student' },
    { value: 'retired', label: 'Retired' },
    { value: 'healthcare-worker', label: 'Healthcare worker' },
    { value: 'essential-worker', label: 'Essential worker' }
  ];

  return (
    <div className="space-y-8">
      {/* Age Input */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Age *
        </label>
        <div className="relative">
          <input
            type="number"
            min="18"
            max="120"
            value={data.age || ''}
            onChange={(e) => handleInputChange('age', parseInt(e.target.value) || '')}
            placeholder="Enter your age"
            className={`form-input ${errors.some(e => e.includes('age')) ? 'border-error focus:ring-error' : ''}`}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <Icon name="Calendar" size={16} color="var(--color-text-secondary)" />
          </div>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          Must be 18 years or older to complete this assessment
        </p>
      </div>

      {/* Gender Selection */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Gender *
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {genderOptions.map((option) => (
            <label
              key={option.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.gender === option.value
                  ? 'border-primary bg-primary-50 text-primary-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="gender"
                value={option.value}
                checked={data.gender === option.value}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.gender === option.value
                  ? 'border-primary bg-primary' :'border-border'
                }
              `}>
                {data.gender === option.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Living Situation */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Current Living Situation
        </label>
        <div className="space-y-2">
          {livingSituationOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.livingSituation || []).includes(option.value)}
                onChange={(e) => handleMultiSelectChange('livingSituation', option.value, e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Employment Status */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Employment Status
        </label>
        <div className="space-y-2">
          {employmentOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.employmentStatus || []).includes(option.value)}
                onChange={(e) => handleMultiSelectChange('employmentStatus', option.value, e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Location (Optional)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              value={data.city || ''}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="City"
              className="form-input"
            />
          </div>
          <div>
            <input
              type="text"
              value={data.country || ''}
              onChange={(e) => handleInputChange('country', e.target.value)}
              placeholder="Country"
              className="form-input"
            />
          </div>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          This helps us provide location-specific resources and recommendations
        </p>
      </div>
    </div>
  );
};

export default DemographicsSection;